import { useGetBeaconRunCoverage, useGetRepositoryFromHalLink } from '../../utils/api-client/api-hooks.ts'
import type { BeaconRun } from '../../utils/api-client/api-schemas.ts'
import { Spinner } from '../spinner.tsx'
import * as styles from './beacon-run-replay.css.ts'
import { BeaconLiveView } from './live/beacon-live-view.tsx'
import { useBeaconRun } from './use-beacon-run.ts'

/**
 * A finished run replayed from its event stream into the live view, up to where it ended; a completed
 * run's stored summary adds the verdict and names the leads it dismissed. Says so when the stream is gone.
 */
export const BeaconRunReplay: React.FC<{ run: BeaconRun }> = ({ run }) => {
  const { run: replayed, settledSeq, connectionStatus, isAvailable } = useBeaconRun(run.id)
  const { data: repository } = useGetRepositoryFromHalLink({ path: run._links.repository.href })
  const { data: summary } = useGetBeaconRunCoverage({ runId: run.id, enabled: run.state === 'completed' })

  if (replayed.seq > 0) {
    return (
      <BeaconLiveView
        run={replayed}
        settledSeq={settledSeq}
        repositoryName={repository?.name ?? run._links.repository.title}
        branch={run.branch ?? repository?.default_branch}
        summary={summary}
      />
    )
  }
  // A stream the platform closed with no frame is as final as one that failed: there is nothing to replay.
  return !isAvailable || connectionStatus === 'error' || connectionStatus === 'finished' ? (
    <p className={styles.note}>The run’s replay is unavailable.</p>
  ) : (
    <div className={styles.loading}>
      <Spinner label="Replaying the run" />
    </div>
  )
}
